/**
 * Pure data-shaping for the "Scheda riepilogo ricevimento merce" export (T-182).
 *
 * No I/O: takes already-fetched event rows + an operator-name resolver and
 * produces the firma/hash model rendered by `receiptPdf.ts`. The cryptographic
 * seals reuse `@trackeat/shared/hash` (`sha256Hex` over `canonicalJson` of the
 * event subset) — the SAME reduction the `export-abbattimento-month` edge
 * function uses, so the two compliance documents are byte-consistent.
 *
 * Read-only over the hash chain: we never recompute or mutate `self_hash`/
 * `prev_hash`; we display what the append-only `events` table already sealed.
 */
import { canonicalJson, sha256Hex } from '@trackeat/shared';

/** Minimal event row needed for the receipt firma block. */
export interface ReceiptEvent {
  server_rev: number;
  kind: string;
  actor_user_id: string | null;
  role: string | null;
  client_ts: string;
  prev_hash: string | null;
  self_hash: string | null;
  payload: Record<string, unknown>;
}

/** Resolves a user id to a display name; returns '(operatore)' when unknown. */
export type NameResolver = (userId: string | null) => string;

export const UNKNOWN_OPERATOR = '(operatore)';

export interface CaricoInfo {
  present: boolean;
  operatore: string;
  ruolo: string;
  dataIso: string | null; // earliest client_ts of the stock_in events
  righe: number;
  revMin: number;
  revMax: number;
  seal: string; // sha256 hex over the stock_in events ('' when none)
}

export interface ConformityInfo {
  present: boolean;
  conform: boolean | null;
  esitoLabel: string; // 'CONFORME' | 'NON CONFORME' | ''
  operatore: string;
  ruolo: string;
  checkedAtIso: string | null;
  notes: string | null;
  disclaimerItems: string[];
  selfHash: string | null;
  rev: number | null;
}

/**
 * Filter only the supplier receipt events. A later StockCommand can retain
 * document_id on rebase, inventory, transfer, production or reverse legs for
 * traceability; none of those is a second receipt of the document.
 */
export function pickStockIn(events: ReceiptEvent[]): ReceiptEvent[] {
  return events
    .filter((e) => {
      if (e.kind !== 'stock_in') return false;
      const movementClass = typeof e.payload.movement_class === 'string'
        ? e.payload.movement_class
        : null;
      const economicEffect = typeof e.payload.economic_effect === 'string'
        ? e.payload.economic_effect
        : null;
      if (movementClass != null || economicEffect != null) {
        return movementClass === 'receive' && economicEffect === 'purchase';
      }
      // Compatibilità con i carichi precedenti a StockCommand, escludendo le
      // gambe tecniche legacy già riconoscibili.
      const reason = typeof e.payload.reason === 'string' ? e.payload.reason : '';
      return reason !== 'lot_correction'
        && reason !== 'uom_rebase'
        && e.payload.uom_rebase_snapshot !== true
        && e.payload.uom_rebase_source !== true;
    })
    .sort((a, b) => a.server_rev - b.server_rev);
}

/** The conformity_check event for this document (latest by server_rev), or null. */
export function pickConformity(events: ReceiptEvent[]): ReceiptEvent | null {
  const matches = events
    .filter((e) => e.kind === 'conformity_check')
    .sort((a, b) => a.server_rev - b.server_rev);
  return matches.length ? matches[matches.length - 1] : null;
}

/**
 * Deterministic SHA-256 seal over a set of events. Each event is reduced to the
 * chain-relevant fields and ordered by server_rev so the digest is stable
 * regardless of fetch order. Returns '' for an empty set.
 */
export async function sealEvents(events: ReceiptEvent[]): Promise<string> {
  if (events.length === 0) return '';
  const reduced = [...events]
    .sort((a, b) => a.server_rev - b.server_rev)
    .map((e) => ({
      server_rev: e.server_rev,
      kind: e.kind,
      prev_hash: e.prev_hash,
      self_hash: e.self_hash,
      payload: e.payload,
    }));
  const digest = await sha256Hex(canonicalJson(reduced));
  return digest;
}

export interface EventRange {
  count: number;
  minRev: number;
  maxRev: number;
}

/** Total event count + server_rev range across all (carico + conformità) events. */
export function eventRange(events: ReceiptEvent[]): EventRange {
  if (events.length === 0) return { count: 0, minRev: 0, maxRev: 0 };
  const revs = events.map((e) => e.server_rev);
  return { count: events.length, minRev: Math.min(...revs), maxRev: Math.max(...revs) };
}

/**
 * Build an operator-name resolver. Names come from `staff_profiles`
 * (tenant-readable); the current user is additionally resolvable from the auth
 * profile (`user_profiles` is self-only, so colleagues are NOT readable there).
 * Anything unresolved degrades to '(operatore)'.
 */
export function buildNameResolver(
  staffNames: Map<string, string>,
  self: { id: string | null; fullName: string | null },
): NameResolver {
  return (id) => {
    if (!id) return UNKNOWN_OPERATOR;
    const staff = staffNames.get(id);
    if (staff) return staff;
    if (id === self.id && self.fullName) return self.fullName;
    return UNKNOWN_OPERATOR;
  };
}

/**
 * T-185: nome operatore "firmato" e snapshottato nell'evento al momento del
 * carico/conformità (payload.operator_name). È la firma al ricevimento: resta
 * leggibile a livello tenant (anche dal consulente che esporta il doc del
 * cliente), indipendentemente da staff_profiles/user_profiles. Null se assente
 * (eventi storici pre-T-185 → si ricade sul resolver staff_profiles/self).
 */
function payloadOperatorName(e: ReceiptEvent): string | null {
  const n = (e.payload as { operator_name?: unknown }).operator_name;
  return typeof n === 'string' && n.trim() ? n.trim() : null;
}

/** Earliest ISO timestamp in a list, or null. */
function minIso(values: Array<string | null | undefined>): string | null {
  let min: string | null = null;
  for (const v of values) {
    if (!v) continue;
    if (min === null || v < min) min = v;
  }
  return min;
}

/** Shape the carico-merce summary (operator, when, line count, rev range, seal). */
export async function shapeCarico(
  stockIns: ReceiptEvent[],
  nameOf: NameResolver,
): Promise<CaricoInfo> {
  if (stockIns.length === 0) {
    return {
      present: false,
      operatore: '',
      ruolo: '',
      dataIso: null,
      righe: 0,
      revMin: 0,
      revMax: 0,
      seal: '',
    };
  }
  const sorted = [...stockIns].sort((a, b) => a.server_rev - b.server_rev);
  const first = sorted[0];
  const revs = sorted.map((e) => e.server_rev);
  return {
    present: true,
    operatore: payloadOperatorName(first) ?? nameOf(first.actor_user_id),
    ruolo: first.role ?? '',
    dataIso: minIso(sorted.map((e) => e.client_ts)),
    righe: sorted.length,
    revMin: Math.min(...revs),
    revMax: Math.max(...revs),
    seal: await sealEvents(sorted),
  };
}

/** Map a conformity flag to its display label. */
export function esitoLabel(conform: boolean | null): string {
  if (conform === true) return 'CONFORME';
  if (conform === false) return 'NON CONFORME';
  return '';
}

/** Shape the conformity-check summary, or an empty `present:false` record. */
export function shapeConformity(
  event: ReceiptEvent | null,
  nameOf: NameResolver,
): ConformityInfo {
  if (!event) {
    return {
      present: false,
      conform: null,
      esitoLabel: '',
      operatore: '',
      ruolo: '',
      checkedAtIso: null,
      notes: null,
      disclaimerItems: [],
      selfHash: null,
      rev: null,
    };
  }
  const p = event.payload as {
    conform?: boolean;
    notes?: string | null;
    checked_at?: string;
    checked_by_user_id?: string;
    disclaimer_items?: unknown;
  };
  const conform = typeof p.conform === 'boolean' ? p.conform : null;
  const items = Array.isArray(p.disclaimer_items)
    ? p.disclaimer_items.filter((x): x is string => typeof x === 'string')
    : [];
  // "Chi": prefer the explicit checked_by_user_id from the payload, fall back to
  // the event's actor (they are the same in normal flow).
  const who = p.checked_by_user_id ?? event.actor_user_id;
  return {
    present: true,
    conform,
    esitoLabel: esitoLabel(conform),
    operatore: payloadOperatorName(event) ?? nameOf(who),
    ruolo: event.role ?? '',
    checkedAtIso: p.checked_at ?? event.client_ts,
    notes: p.notes ?? null,
    disclaimerItems: items,
    selfHash: event.self_hash,
    rev: event.server_rev,
  };
}

/** "01/06/2026" from an ISO string; '' when absent/invalid. */
export function formatDateIt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** "01/06/2026 14:32" from an ISO string; '' when absent/invalid. */
export function formatDateTimeIt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
